/* ═══════════════════════════════════════════════════════════════════════════
   Telemetry — seeing failures without being told about them
   ───────────────────────────────────────────────────────────────────────────
   This file is copied verbatim between `hexclean`, `hexclean-partner` and
   `hexclean-admin`, like `lib/shared/`. It reads `config.app` for the one
   thing that differs. Fix a bug here and it has to be carried across; that is
   the price of three repos with no package between them, and it is cheaper
   than the alternative of three drifting copies.

   **Crashlytics is not part of this, and cannot be.** There is no Crashlytics
   web SDK — it exists for Android and iOS only, and no amount of configuration
   will make a browser error appear in that console. What follows is the thing
   Crashlytics would have been:

   · **Firebase Analytics** takes an `exception` event, which is what makes an
     error show up next to the behaviour that caused it — sessions, screens,
     funnels. It carries no stack trace: GA4 caps a parameter at 100 characters
     and drops anything longer without complaint. So Analytics answers *how
     often, on which screen, to how many people*.
   · **`hexclean-api`** takes the whole report — stack, component stack,
     breadcrumbs, build id — and the Issues screen in the admin panel reads it
     back. That answers *what actually broke*.

   Both, because either alone is half a picture. Neither is allowed to fail
   loudly: a telemetry module that throws is strictly worse than no telemetry,
   so every path here swallows its own errors. The console is the last resort
   and it only ever gets a warning.

   ── what is deliberately not sent ──────────────────────────────────────────
   Reports go to our own server and are read by staff, which is not a reason to
   be careless. Anything that could carry a customer's identity is removed
   before a report is queued, not after: query strings go, path segments that
   look like ids become `:id`, and long digit runs — a phone number, a card, an
   ID number — are masked wherever they appear, including inside a stack trace.
   `redact()` is the one place that happens, so there is one place to audit.

   The user id is sent, and it is the opaque account id rather than a number or
   an address. "Which of these is only me" is the first question about any bug
   report, and an id that means nothing outside our own database answers it
   without carrying anything about the person.
   ═══════════════════════════════════════════════════════════════════════════ */

import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { config } from './config'

/* ── the shapes on the wire ────────────────────────────────────────────────── */

/** Where a report came from. Worth distinguishing because they fail
 *  differently: a `react` error killed a screen, an `unhandledrejection` is
 *  usually a dropped promise that left a spinner running, and a `resource`
 *  error means an asset 404'd — often a stale build rather than a code bug. */
export type ReportKind = 'error' | 'unhandledrejection' | 'react' | 'manual' | 'resource'

/** A step on the way to the failure.
 *
 *  A stack trace says which line threw. It never says that the operator had
 *  just switched branch filter, or that the three requests before it were
 *  503s. That context is what turns a report into a reproduction, and it is
 *  almost always the thing a bug report leaves out. */
export interface Breadcrumb {
  at: string
  category: 'navigation' | 'request' | 'action' | 'state'
  message: string
  data?: Record<string, string | number | boolean>
}

export interface ErrorReport {
  /** Stable across occurrences of the same bug, so the Issues screen can group
   *  five thousand of them into one row. */
  fingerprint: string
  app: string
  release: string
  kind: ReportKind
  message: string
  stack?: string
  componentStack?: string
  /** The route with ids masked — `/orders/:id`, never `/orders/ord_8813`. */
  screen: string
  userId?: string
  role?: string
  /** Groups every report from one page-load, so an error that cascades reads
   *  as one incident rather than nine. */
  sessionId: string
  occurredAt: string
  /** How many times this fingerprint fired before the batch was sent. A render
   *  loop is one report with a count of four hundred, not four hundred
   *  reports. */
  count: number
  breadcrumbs: Breadcrumb[]
  userAgent: string
  viewport: string
  online: boolean
  locale: string
}

/* ── knobs ─────────────────────────────────────────────────────────────────── */

/** Ring buffer size. Enough to cover the last few screens and requests; not so
 *  much that a report becomes a log file. */
const BREADCRUMB_LIMIT = 25

/** Reports held before a flush. Small, because the interesting failure is
 *  usually the one that takes the page with it. */
const BATCH_LIMIT = 10

/** How long a batch is allowed to sit. */
const FLUSH_MS = 5_000

/** Distinct fingerprints per page-load. A ceiling rather than a target: past
 *  this, something is looping and the first twenty already described it. Also
 *  the thing that stops a broken build from turning every open tab into a
 *  request generator pointed at the API. */
const SESSION_REPORT_LIMIT = 20

/** Occurrences of one fingerprint that still produce a network send. Beyond
 *  it the count keeps rising locally and is reported once. */
const PER_FINGERPRINT_LIMIT = 5

/* ── state ─────────────────────────────────────────────────────────────────── */

const sessionId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`

const breadcrumbs: Breadcrumb[] = []
const pending: ErrorReport[] = []
const seen = new Map<string, number>()

let started = false
let flushTimer: ReturnType<typeof setTimeout> | undefined
let user: { id?: string; role?: string } = {}

/** Resolved once `firebase/analytics` has loaded and reported itself
 *  supported. Null forever in a build with no measurement id, which is every
 *  build until Analytics is switched on for the Firebase project. */
let analytics: import('firebase/analytics').Analytics | null = null

/** Values from Remote Config, read synchronously by `flag()`. Populated once
 *  on boot; a fetch that fails leaves the defaults in place, which is the
 *  whole point of holding them here rather than reading through to the SDK. */
let flags: Record<string, string> = {}

/* ── redaction ─────────────────────────────────────────────────────────────── */

/** Path segments that are data rather than route.
 *
 *  Ids in this system are prefixed (`ord_`, `cus_`, `prt_`), but a bare uuid
 *  or a bare number turns up too — and `/customers/966501234567` is a phone
 *  number sitting in a URL. Matching on shape rather than on a list of known
 *  prefixes is what keeps this correct when a new entity type is added and
 *  nobody remembers this file exists. */
const IDLIKE = /^(?:[a-z]{2,4}_[A-Za-z0-9]+|[0-9a-f]{8}-[0-9a-f-]{27,}|\d{3,}|[A-Za-z0-9_-]{20,})$/

/** Six or more consecutive digits, anywhere.
 *
 *  A phone number, an ID number, a card. Six is low enough to catch a Saudi
 *  mobile in any of the forms it gets typed in and high enough to leave a
 *  timestamp in milliseconds alone — those are thirteen digits, so they are
 *  masked too, which costs nothing. */
const LONG_DIGITS = /\d{6,}/g

/** Anything that reads as a credential. Tokens end up in error messages more
 *  often than anyone expects — a failed request that logs its own headers, an
 *  auth library quoting the value it could not parse. */
const SECRETS = /\b(?:bearer\s+[\w-]+\.[\w-]+\.[\w-]+|eyJ[\w-]{10,}\.[\w-]+\.[\w-]+|AIza[\w-]{20,})/gi

export function redact(text: string): string {
  return text
    .replace(SECRETS, '[redacted]')
    /* Query strings are dropped whole rather than filtered. A allowlist would
       need updating every time a screen gains a filter, and the one time it is
       forgotten is the time it carries an address. */
    .replace(/([?&])[^\s"')]+/g, '$1[query]')
    .replace(LONG_DIGITS, '[number]')
}

/** A pathname reduced to its route. `/orders/ord_8813/refund` → `/orders/:id/refund`. */
export function screenOf(pathname: string): string {
  return (
    pathname
      .split('/')
      .map((segment) => (IDLIKE.test(segment) ? ':id' : segment))
      .join('/') || '/'
  )
}

/* ── fingerprinting ────────────────────────────────────────────────────────── */

/** FNV-1a. Not a security hash — this only has to be stable and cheap, and it
 *  runs on a path where the app is already in trouble. */
function hash(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(36)
}

/** What makes two occurrences "the same bug".
 *
 *  The message with its variables removed, plus the first stack frame that is
 *  ours. Without the normalisation, `Cannot read properties of undefined
 *  (reading 'name')` at two different call sites would group together while
 *  the same bug on two different order ids would not — both wrong, in opposite
 *  directions. */
function fingerprintOf(kind: ReportKind, message: string, stack: string | undefined): string {
  const normalised = message
    .replace(LONG_DIGITS, 'N')
    .replace(/\b\d+\b/g, 'N')
    .replace(/https?:\/\/[^\s"')]+/g, 'URL')
    .replace(/['"`][^'"`]{0,80}['"`]/g, 'S')
    .trim()

  /* The first frame naming a file we shipped. Frames inside node_modules or
     the browser's own internals are shared by unrelated bugs, and grouping on
     one of those merges everything that ever went through React's scheduler. */
  const frame =
    stack
      ?.split('\n')
      .slice(1)
      .map((line) => line.trim())
      .find((line) => line.includes('/assets/') || line.includes('/src/'))
      ?.replace(/:\d+:\d+/g, '')
      ?.replace(/https?:\/\/[^/]+/g, '') ?? ''

  return hash(`${config.app}|${kind}|${normalised}|${frame}`)
}

/* ── breadcrumbs ───────────────────────────────────────────────────────────── */

export function addBreadcrumb(
  category: Breadcrumb['category'],
  message: string,
  data?: Record<string, string | number | boolean>,
): void {
  try {
    breadcrumbs.push({
      at: new Date().toISOString(),
      category,
      message: redact(message).slice(0, 200),
      ...(data ? { data } : {}),
    })
    if (breadcrumbs.length > BREADCRUMB_LIMIT) breadcrumbs.shift()
  } catch {
    /* A breadcrumb is a nicety. It must never be the thing that breaks. */
  }
}

/* ── the sinks ─────────────────────────────────────────────────────────────── */

/** Where full reports are posted. Empty in mock builds, which have no server
 *  to post to — development failures belong in the console, where they are
 *  already visible and immediate. */
const endpoint =
  config.telemetryUrl !== ''
    ? config.telemetryUrl
    : config.apiMode === 'http'
      ? `${config.apiUrl}/telemetry/errors`
      : ''

function post(reports: ErrorReport[]): void {
  if (endpoint === '' || reports.length === 0) return
  const body = JSON.stringify({ reports })

  /* `sendBeacon` first, and not as an optimisation: the report that matters
     most is the one from the page-load that is ending, and a `fetch` issued
     during `pagehide` is cancelled when the document goes away. A beacon is
     handed to the browser, which delivers it after the page is gone.
     `keepalive` on fetch is the fallback and has a 64KB ceiling, which is why
     batches are small.

     **`text/plain`, and it has to be.** The API is on another origin, and
     `application/json` is not a CORS-safelisted content type — so a beacon
     sent with it is a preflighted request, and a preflight is a second round
     trip that has to complete while the page is being torn down. It usually
     does not, and the failure is invisible: `sendBeacon` returns true because
     the request was queued, and the report is simply never delivered. A
     safelisted type makes it a simple request with no preflight at all. The
     body is still JSON and the server still parses it as JSON — Go's decoder
     reads the body, not the Content-Type header. */
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
      if (navigator.sendBeacon(endpoint, blob)) return
    }
  } catch {
    /* Fall through to fetch. */
  }

  try {
    void fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      /* No credentials and no auth header. This endpoint is deliberately
         unauthenticated — an error that happens before or during sign-in is
         exactly the one worth having, and a report is not a privileged read. */
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {
      /* The network is a thing that fails, and this is the module that exists
         because things fail. Losing a report is acceptable; a rejected promise
         escaping from here and triggering `unhandledrejection` is not — that
         is an infinite loop with a network request in it. */
    })
  } catch {
    /* Ignored, as above. */
  }
}

function flush(): void {
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer)
    flushTimer = undefined
  }
  if (pending.length === 0) return
  post(pending.splice(0, pending.length))
}

function scheduleFlush(): void {
  if (pending.length >= BATCH_LIMIT) {
    flush()
    return
  }
  if (flushTimer !== undefined) return
  flushTimer = setTimeout(flush, FLUSH_MS)
}

/* ── capture ───────────────────────────────────────────────────────────────── */

/** Pulls a message and a stack out of whatever was thrown.
 *
 *  `throw 'oops'` is legal, and so is throwing an object with no `message`.
 *  Both turn up in real code — usually from a library — and both would
 *  otherwise be reported as `[object Object]`, which is a report nobody can
 *  act on. */
function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: `${error.name}: ${error.message}`,
      ...(error.stack ? { stack: error.stack } : {}),
    }
  }
  if (typeof error === 'string') return { message: error }
  if (error && typeof error === 'object') {
    const shape = error as { message?: unknown; code?: unknown }
    if (typeof shape.message === 'string') {
      return {
        message:
          typeof shape.code === 'string' ? `${shape.code}: ${shape.message}` : shape.message,
      }
    }
    try {
      return { message: JSON.stringify(error).slice(0, 300) }
    } catch {
      return { message: '[unserialisable error]' }
    }
  }
  return { message: String(error) }
}

/* ── what is not worth waking anybody for ─────────────────────────────────────
   Three of the six issues on `GET /admin/issues` were not bugs in this product
   and could not be fixed by changing it. They cost triage anyway, because an
   Issues screen is only read while the things on it are real.

     - **`Error: Database is closing/hidden`**, thrown from deep inside
       firebase-auth's IndexedDB persistence — `_openDb` → `_withRetries` →
       `setCurrentUser`. It happens when the tab is hidden or closed while auth
       is writing its own state back. The SDK retries; nothing in this app is
       broken, and there is no line here to change.
     - **`Error: Rejected`** from `registerSW.js`. The service worker failing to
       register is a browser or a private window declining, and the app already
       works without one — a PWA reporting its own optional upgrade as a defect.
     - **`ResizeObserver loop …`**, which every browser emits and no user sees.

   Dropped by *cause* rather than by kind: an `unhandledrejection` is exactly
   how a real bug in an async path arrives, so dropping the kind would lose the
   reports worth having. Each entry names the thing it silences and why, and
   nothing here matches a message this codebase produces. */
const NOT_OURS: readonly { pattern: RegExp; why: string }[] = [
  {
    pattern: /database is closing|database is hidden|idbdatabase|indexeddb.*clos/i,
    why: 'firebase-auth persisting to IndexedDB as the tab goes away',
  },
  { pattern: /^error: rejected$/i, why: 'service worker registration declined' },
  { pattern: /failed to register a serviceworker|registerSW/i, why: 'the same, named' },
  { pattern: /resizeobserver loop/i, why: 'emitted by the browser, invisible to the user' },
  {
    pattern: /^(script error|error)\.?$/i,
    why: 'a cross-origin script with no message to report',
  },
]

/** Whether a report is about this product at all.
 *
 *  Checked against the message *and* the stack: `Error: Rejected` says nothing
 *  on its own, and it is `registerSW.js` one frame down that identifies it. */
export function notOurs(message: string, stack?: string): string | null {
  const haystack = stack ? [message, stack].join(' ') : message
  for (const { pattern, why } of NOT_OURS) {
    if (pattern.test(message) || pattern.test(haystack)) return why
  }
  return null
}

export interface CaptureContext {
  kind?: ReportKind
  componentStack?: string
  /** Extra breadcrumb recorded alongside the report — where the caller knows
   *  something the stack does not, e.g. which mutation was in flight. */
  context?: Record<string, string | number | boolean>
}

/** The entry point everything else funnels into. Safe to call from anywhere,
 *  including from inside a failure. Never throws. */
export function captureError(error: unknown, options: CaptureContext = {}): void {
  try {
    const { kind = 'manual', componentStack, context } = options
    const described = describe(error)
    const message = redact(described.message).slice(0, 500)
    const stack = described.stack ? redact(described.stack).slice(0, 4_000) : undefined

    /* Dropped before anything is counted, so a noisy third party cannot spend
       this session's twenty fingerprints and push a real bug off the end. Still
       logged in development, where the reason is the useful half. */
    const foreign = notOurs(message, stack)
    if (foreign) {
      if (config.isDev) console.info(`[telemetry] ignored — ${foreign}`, message)
      return
    }

    const fingerprint = fingerprintOf(kind, message, described.stack)

    const before = seen.get(fingerprint) ?? 0
    seen.set(fingerprint, before + 1)

    /* Loud in development, where the console is being watched and a network
       round trip would only add latency to a stack trace already on screen. */
    if (config.isDev) {
      console.warn(`[telemetry] ${kind}`, error, componentStack ?? '')
    }

    if (before >= PER_FINGERPRINT_LIMIT) return
    if (before === 0 && seen.size > SESSION_REPORT_LIMIT) return

    /* Analytics gets every occurrence, because counting is the one thing it is
       genuinely better at, and an `exception` event is small. */
    logAnalytics('exception', {
      description: message.slice(0, 100),
      fatal: kind === 'react',
      hex_fingerprint: fingerprint,
      hex_screen: currentScreen(),
    })

    if (context) addBreadcrumb('state', 'context', context)

    pending.push({
      fingerprint,
      app: config.app,
      release: config.version,
      kind,
      message,
      ...(stack ? { stack } : {}),
      ...(componentStack ? { componentStack: redact(componentStack).slice(0, 2_000) } : {}),
      screen: currentScreen(),
      ...(user.id ? { userId: user.id } : {}),
      ...(user.role ? { role: user.role } : {}),
      sessionId,
      occurredAt: new Date().toISOString(),
      count: before + 1,
      breadcrumbs: breadcrumbs.slice(-BREADCRUMB_LIMIT),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
      viewport:
        typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
      online: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
      locale: typeof document !== 'undefined' ? document.documentElement.lang || 'en' : 'en',
    })

    scheduleFlush()
  } catch {
    /* The one place a throw would be genuinely damaging — an error handler
       that throws inside `window.onerror` re-enters itself. */
  }
}

function currentScreen(): string {
  return typeof window !== 'undefined' ? screenOf(window.location.pathname) : ''
}

/* ── Firebase Analytics ────────────────────────────────────────────────────── */

/** Reuses whatever `identity.ts` already built. Two `initializeApp` calls with
 *  the same name throw, and this module may well run first. */
function firebaseApp(): FirebaseApp | null {
  if (config.firebase === null) return null
  try {
    return getApps()[0] ?? initializeApp(config.firebase)
  } catch {
    return null
  }
}

/** Analytics is genuinely optional and is expected to be off.
 *
 *  It needs a `measurementId`, which only exists once Google Analytics is
 *  linked to the Firebase project — a console action nobody has taken yet.
 *  Until then this resolves to null and every `logAnalytics` call is a no-op,
 *  which is the correct behaviour rather than a degraded one: the API sink
 *  carries the reports, and Analytics adds audience numbers to them later. */
async function initAnalytics(): Promise<void> {
  const firebase = config.firebase
  if (firebase === null || (firebase.measurementId ?? '') === '') return

  try {
    /* Dynamically imported so the SDK lands in its own chunk. Statically
       importing it would put ~40KB of analytics into the entry bundle, which
       every user pays for on first paint and which is not needed to render
       anything. */
    const { getAnalytics, isSupported, setUserId, setUserProperties } = await import(
      'firebase/analytics'
    )
    if (!(await isSupported())) return

    const app = firebaseApp()
    if (app === null) return

    analytics = getAnalytics(app)
    setUserProperties(analytics, { hex_app: config.app, hex_release: config.version })
    if (user.id) setUserId(analytics, user.id)
  } catch {
    analytics = null
  }
}

function logAnalytics(name: string, params: Record<string, string | number | boolean>): void {
  if (analytics === null) return
  try {
    void import('firebase/analytics').then(({ logEvent }) => {
      if (analytics !== null) logEvent(analytics, name, params)
    })
  } catch {
    /* Analytics is the sink we can afford to lose. */
  }
}

/* ── Firebase Performance ──────────────────────────────────────────────────── */

/** Page load, first paint and every fetch, measured by the browser and
 *  collected without a single call site.
 *
 *  Worth having precisely because it needs no instrumentation: "the orders
 *  screen got slow last Tuesday" is a question nobody can answer from logs,
 *  and it is answered here for the cost of an import. */
async function initPerformance(): Promise<void> {
  if (!config.performance) return
  const app = firebaseApp()
  if (app === null) return
  try {
    const { getPerformance } = await import('firebase/performance')
    getPerformance(app)
  } catch {
    /* Blocked by an extension, or unsupported. Not worth a word. */
  }
}

/* ── Firebase Remote Config ────────────────────────────────────────────────── */

/** Values that can change without a deploy.
 *
 *  The reason this is here rather than in its own module is that it shares the
 *  failure policy: a flag that cannot be fetched must resolve to the default
 *  it was compiled with, silently, every time. A feature flag system that can
 *  break the app is a worse deal than no feature flags.
 *
 *  Read synchronously through `flag()` after boot, so a component never has to
 *  handle a loading state for what is, from its point of view, a constant. */
async function initRemoteConfig(defaults: Record<string, string>): Promise<void> {
  flags = { ...defaults }
  const app = firebaseApp()
  if (app === null) return
  try {
    const { fetchAndActivate, getAll, getRemoteConfig } = await import('firebase/remote-config')
    const remote = getRemoteConfig(app)
    remote.defaultConfig = defaults
    /* Twelve hours in production — the SDK throttles server-side anyway, and a
       flag is a dial rather than a switch anybody waits on. A minute in
       development, so a change can actually be tried. */
    remote.settings.minimumFetchIntervalMillis = config.isDev ? 60_000 : 12 * 3_600_000
    await fetchAndActivate(remote)
    const all = getAll(remote)
    for (const [key, value] of Object.entries(all)) flags[key] = value.asString()
  } catch {
    /* Defaults stand. */
  }
}

/** A Remote Config value, or the default this build was compiled with. */
export function flag(key: string, fallback = ''): string {
  return flags[key] ?? fallback
}

export function flagEnabled(key: string, fallback = false): boolean {
  const value = flags[key]
  if (value === undefined) return fallback
  return value === 'true' || value === '1'
}

/* ── the public surface ────────────────────────────────────────────────────── */

/** Names the signed-in account for everything reported afterwards.
 *
 *  Called from the session layer on sign-in and with `null` on sign-out.
 *  Clearing matters on the admin panel especially: a shared machine must not
 *  attribute the next operator's errors to the last one. */
export function setTelemetryUser(id: string | null, role?: string): void {
  user = id === null ? {} : { id, ...(role ? { role } : {}) }
  if (analytics === null) return
  try {
    void import('firebase/analytics').then(({ setUserId, setUserProperties }) => {
      if (analytics === null) return
      setUserId(analytics, id)
      if (role) setUserProperties(analytics, { hex_role: role })
    })
  } catch {
    /* No-op. */
  }
}

/** A screen view. Called on every route change.
 *
 *  The path is masked before it goes anywhere — `page_path` on an order detail
 *  screen would otherwise put an order id into Analytics, where it is neither
 *  useful nor ours to keep. */
export function trackScreen(pathname: string, title?: string): void {
  const screen = screenOf(pathname)
  addBreadcrumb('navigation', screen)
  logAnalytics('page_view', {
    page_path: screen,
    page_title: title ?? (typeof document !== 'undefined' ? document.title : ''),
    hex_app: config.app,
  })
}

/** A deliberate product event — an order placed, an offer accepted, a payout
 *  run executed. Kept to the things somebody would actually ask about; an
 *  analytics property that logs everything answers nothing. */
export function trackEvent(
  name: string,
  params: Record<string, string | number | boolean> = {},
): void {
  addBreadcrumb('action', name, params)
  logAnalytics(name, { ...params, hex_app: config.app })
}

/** Records a failed request as a breadcrumb, and a server error as a report.
 *
 *  Called from the HTTP client. The distinction is the point: a 404 or a 422
 *  is the server working correctly and belongs in the trail behind whatever
 *  happens next, while a 5xx or a dropped connection is a failure in its own
 *  right and is worth a report even though nothing threw at the user. */
export function trackRequestFailure(
  method: string,
  path: string,
  status: number | 'network' | 'timeout',
  message: string,
): void {
  const screen = screenOf(path)
  addBreadcrumb('request', `${method} ${screen} → ${status}`, { message: redact(message) })
  if (status === 'network') return
  if (status === 'timeout' || (typeof status === 'number' && status >= 500)) {
    captureError(new Error(`${method} ${screen} failed: ${status} ${message}`), {
      kind: 'error',
      context: { method, path: screen, status: String(status) },
    })
  }
}

/* ── boot ──────────────────────────────────────────────────────────────────── */

export interface TelemetryOptions {
  /** Compiled-in Remote Config values. Every key the app reads must appear
   *  here — `flag()` returns its own fallback otherwise, and two fallbacks for
   *  one key is how they end up disagreeing. */
  flags?: Record<string, string>
}

/** Installs the global handlers and starts the SDKs. Call once, before the
 *  first render, so an error thrown during boot is still caught. */
export function initTelemetry(options: TelemetryOptions = {}): void {
  if (started || typeof window === 'undefined') return
  started = true

  /* Anything thrown and not caught, including from inside a callback React
     never sees. */
  window.addEventListener('error', (event: ErrorEvent) => {
    /* A failed `<img>` or `<script>` fires the same event type with no `error`
       on it. Reported as `resource` rather than dropped: on a PWA behind a
       service worker, a 404 on a hashed asset means the build moved under a
       stale worker, which is a real failure with no exception attached. */
    if ((event.error === null || event.error === undefined) && event.target !== window) {
      const el = event.target as { tagName?: string; src?: string; href?: string } | null
      const url = el?.src ?? el?.href
      if (url) {
        captureError(new Error(`Failed to load ${el?.tagName ?? 'resource'}: ${redact(url)}`), {
          kind: 'resource',
        })
      }
      return
    }
    captureError(event.error ?? event.message, { kind: 'error' })
  }, true)

  /* The one that is invisible without this line. A rejected promise nobody
     awaited produces no user-visible error and no console entry in production
     — the screen simply never finishes loading. */
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    captureError(event.reason, { kind: 'unhandledrejection' })
  })

  /* Flushed when the page is hidden, not on `unload`. `unload` does not fire
     reliably on mobile Safari or on a backgrounded tab that is killed, which
     between them are most of this product's traffic. `visibilitychange` is the
     event that does fire, every time. */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  window.addEventListener('pagehide', flush)

  addBreadcrumb('navigation', currentScreen())

  /* Fire and forget, all three. None of them blocks the first render, and a
     rejection here is already handled inside each. */
  void initAnalytics()
  void initPerformance()
  void initRemoteConfig(options.flags ?? {})
}

/** Exposed for tests, which need a way to assert on what would have been sent
 *  without waiting five seconds for a timer. Not part of the module's real
 *  surface — nothing in the apps imports it. */
export const telemetryInternals = {
  flush,
  pending,
  reset(): void {
    pending.length = 0
    breadcrumbs.length = 0
    seen.clear()
    user = {}
    started = false
    if (flushTimer !== undefined) {
      clearTimeout(flushTimer)
      flushTimer = undefined
    }
  },
  endpoint,
  sessionId,
}
